import { ReactJSXElement } from "@emotion/react/types/jsx-namespace";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { Box, Card, Chip, Typography, useColorScheme } from "@mui/joy";
import { useMediaQuery, useTheme } from "@mui/material";
import ReactPlayer from "react-player/lazy";

export interface RealmData {
  title: string;
  description: string;
  imageComponent: ReactJSXElement;
  capital: string;
  videoUrl: string;
  epithet: string;
  shortVideos?: {
    title: string;
    link: string;
  }[];
}

type Props = {
  realm: RealmData;
};

export default function RealmsBanner(props: Props) {
  const {
    title,
    description,
    imageComponent,
    capital,
    videoUrl,
    epithet,
    shortVideos,
  } = props.realm;
  const theme = useTheme();
  const { mode } = useColorScheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = mode === "dark";

  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        maxWidth: "1200px",
        margin: "2rem auto",
        background: isDark ? "rgba(0, 0, 0, 0.4)" : "rgba(255, 255, 255, 0.6)",
        border: isDark
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
        backdropFilter: "blur(10px)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Background gradient overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.05) 0%, transparent 50%)",
          zIndex: -1,
        }}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 3,
          p: 4,
        }}
      >
        {/* Video Section */}
        <Box
          sx={{
            flex: isMobile ? "none" : "0 0 400px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {videoUrl === "Soon" ? (
            <Box
              sx={{
                width: "100%",
                height: "225px",
                background: isDark
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.05)",
                border: isDark
                  ? "1px solid rgba(255, 255, 255, 0.1)"
                  : "1px solid rgba(0, 0, 0, 0.1)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(5px)",
              }}
            >
              <Typography
                level="body-lg"
                sx={{
                  color: isDark
                    ? "rgba(255, 255, 255, 0.6)"
                    : "rgba(30, 41, 59, 0.6)",
                  textAlign: "center",
                }}
              >
                Video Coming Soon!
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
              }}
            >
              <ReactPlayer
                url={videoUrl}
                light
                width="100%"
                height="225px"
                style={{
                  borderRadius: "12px",
                }}
              />
            </Box>
          )}
        </Box>

        {/* Content Section */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
              gap: 2,
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "2px solid rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255, 255, 255, 0.05)",
                  "& img": {
                    width: "60px",
                    height: "60px",
                    objectFit: "contain",
                  },
                }}
              >
                {imageComponent}
              </Box>
              <Box>
                <Typography
                  level="h4"
                  sx={{
                    color: isDark ? "white" : "#1e293b",
                    fontWeight: 300,
                    letterSpacing: "0.02em",
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  level="body-sm"
                  sx={{
                    color: isDark
                      ? "rgba(255, 255, 255, 0.6)"
                      : "rgba(30, 41, 59, 0.6)",
                    fontStyle: "italic",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    fontSize: "0.75rem",
                  }}
                >
                  {epithet}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Capital City */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
            }}
          >
            <Typography
              level="title-sm"
              sx={{
                color: isDark
                  ? "rgba(255, 255, 255, 0.8)"
                  : "rgba(30, 41, 59, 0.8)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                fontSize: "0.8rem",
              }}
            >
              Capital City:
            </Typography>
            <Typography
              level="body-lg"
              sx={{
                color: isDark ? "white" : "#1e293b",
                fontWeight: 500,
              }}
            >
              {capital}
            </Typography>
          </Box>

          {/* Description */}
          <Typography
            level="body-md"
            sx={{
              color: isDark
                ? "rgba(255, 255, 255, 0.8)"
                : "rgba(30, 41, 59, 0.8)",
              lineHeight: 1.7,
              textAlign: "justify",
              mb: 3,
            }}
          >
            {description}
          </Typography>

          {/* Short Videos */}
          {shortVideos && shortVideos.length > 0 && (
            <Box>
              <Typography
                level="title-sm"
                sx={{
                  color: isDark ? "white" : "#1e293b",
                  mb: 2,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontSize: "0.8rem",
                }}
              >
                Related Content
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                {shortVideos.map((videoData, index) => {
                  const isYouTubeLink =
                    videoData.link.includes("youtube") ||
                    videoData.link.includes("youtu.be");
                  return (
                    <Chip
                      key={videoData.title}
                      variant="outlined"
                      onClick={() => window.open(videoData.link, "_blank")}
                      startDecorator={
                        isYouTubeLink ? (
                          <YouTubeIcon sx={{ fontSize: "1rem" }} />
                        ) : undefined
                      }
                      sx={{
                        background: isDark
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.05)",
                        border: isDark
                          ? "1px solid rgba(255, 255, 255, 0.2)"
                          : "1px solid rgba(0, 0, 0, 0.2)",
                        color: isDark
                          ? "rgba(255, 255, 255, 0.8)"
                          : "rgba(30, 41, 59, 0.8)",
                        fontSize: "0.75rem",
                        fontFamily: "'EB Garamond', serif",
                        cursor:
                          videoData.link === "Soon" ? "not-allowed" : "pointer",
                        opacity: videoData.link === "Soon" ? 0.5 : 1,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background:
                            videoData.link !== "Soon"
                              ? "rgba(255, 255, 255, 0.1)"
                              : undefined,
                          border:
                            videoData.link !== "Soon"
                              ? "1px solid rgba(255, 255, 255, 0.4)"
                              : undefined,
                          transform:
                            videoData.link !== "Soon"
                              ? "translateY(-1px)"
                              : undefined,
                        },
                      }}
                    >
                      {videoData.title}
                    </Chip>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
}
